import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { AgentPerformanceReport } from '../../types/reports';

interface AgentPerformanceChartProps {
  data: AgentPerformanceReport;
}

const AgentPerformanceChart: React.FC<AgentPerformanceChartProps> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const getDateLocale = () => {
    const lang = i18n.language;
    if (lang === 'pt-BR') return 'pt-BR';
    if (lang === 'es-ES') return 'es-ES';
    return 'en-US';
  };

  const dailyData = data.dailyStats.map(stat => ({
    ...stat,
    date: new Date(stat.date).toLocaleDateString(getDateLocale(), { 
      day: '2-digit', 
      month: '2-digit' 
    })
  }));

  const agentData = data.agentStats.map(stat => ({
    ...stat,
    agentName: stat.agentName.length > 15 
      ? stat.agentName.substring(0, 15) + '...' 
      : stat.agentName
  }));

  const typeProductData = data.typeProductBreakdown.map(item => ({
    ...item,
    name: item.typeProductName
  }));

  const statusData = [
    { name: t('pages.reports.charts.agentPerformance.active'), value: data.activeAgents, color: theme.palette.success.main },
    { name: t('pages.reports.charts.agentPerformance.disabled'), value: data.disabledAgents, color: theme.palette.error.main }
  ];

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%', 
      overflow: 'hidden',
      px: isMobile ? 0 : 0
    }}>
      {/* Summary Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: isMobile ? 1 : 3, 
        mb: isMobile ? 2 : 4,
        justifyContent: isMobile ? 'center' : 'flex-start'
      }}>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
                {t('pages.reports.charts.agentPerformance.totalAgents')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {data.totalAgents}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
                {t('pages.reports.charts.agentPerformance.activeAgents')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {data.activeAgents}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
                {t('pages.reports.charts.agentPerformance.completionRate')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {data.completionRate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
                {t('pages.reports.charts.agentPerformance.averageResponseTime')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'info.main' }}>
                {data.averageResponseTime.toFixed(0)}ms
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Charts */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: isMobile ? 2 : 3,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        {/* Daily Performance Chart */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap', 
          gap: isMobile ? 2 : 3,
          width: '100%',
          maxWidth: '100%'
        }}>
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 600px', 
            minWidth: isMobile ? '100%' : '600px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.agentPerformance.dailyPerformance')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 300}>
                <AreaChart data={dailyData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="activeAgents"
                    stackId="1"
                    stroke={theme.palette.primary.main}
                    fill={theme.palette.primary.main}
                    fillOpacity={0.6}
                    name={t('pages.reports.charts.agentPerformance.activeAgentsLabel')}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalConversations"
                    stackId="2"
                    stroke={theme.palette.success.main}
                    fill={theme.palette.success.main}
                    fillOpacity={0.6}
                    name={t('pages.reports.charts.agentPerformance.totalConversations')}
                  />
                  <Area
                    type="monotone"
                    dataKey="completedConversations"
                    stackId="3"
                    stroke={theme.palette.warning.main}
                    fill={theme.palette.warning.main}
                    fillOpacity={0.6}
                    name={t('pages.reports.charts.agentPerformance.completedConversations')}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* Agent Status Distribution */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 300px', 
            minWidth: isMobile ? '100%' : '300px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.agentPerformance.agentStatus')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Box>
        </Box>

        {/* Agent Performance Comparison */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap', 
          gap: isMobile ? 2 : 3,
          width: '100%',
          maxWidth: '100%'
        }}>
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 600px', 
            minWidth: isMobile ? '100%' : '600px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.agentPerformance.performanceComparison')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 300}>
                <BarChart data={agentData} layout="horizontal" margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="agentName" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="totalConversations" fill={theme.palette.primary.main} name={t('pages.reports.charts.agentPerformance.totalConversationsBar')} />
                  <Bar dataKey="completedConversations" fill={theme.palette.success.main} name={t('pages.reports.charts.agentPerformance.completedConversationsBar')} />
                  <Bar dataKey="totalLeads" fill={theme.palette.warning.main} name={t('pages.reports.charts.agentPerformance.totalLeadsBar')} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* Type Product Breakdown */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 300px', 
            minWidth: isMobile ? '100%' : '300px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.agentPerformance.productTypeDistribution')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {typeProductData.map((type, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{type.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {type.count}
                      </Typography>
                      <Chip
                        label={`${type.percentage.toFixed(1)}%`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Response Time Chart */}
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          <Paper sx={{ 
            p: isMobile ? 1 : 3,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('pages.reports.charts.agentPerformance.responseTimeByAgent')}
            </Typography>
            <ResponsiveContainer width="100%" height={isMobile ? 280 : 300}>
              <BarChart data={agentData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="agentName" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="averageResponseTime" fill={theme.palette.info.main} name={t('pages.reports.charts.agentPerformance.averageResponseTimeLabel')} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Box>

        {/* Agent Details Table */}
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          <Paper sx={{ 
            p: isMobile ? 1 : 3,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('pages.reports.charts.agentPerformance.agentDetails')}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.agentPerformance.agent')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.agentPerformance.status')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.agentPerformance.conversations')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.agentPerformance.completed')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.agentPerformance.completionRateLabel')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.agentPerformance.responseTime')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.agentPerformance.productType')}</th>
                  </tr>
                </thead>
                <tbody>
                  {agentData.map((agent, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px' }}>{agent.agentName}</td>
                      <td style={{ padding: '12px' }}>
                        <Chip
                          label={agent.status}
                          size="small"
                          color={agent.status === 'Enabled' ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </td>
                      <td style={{ padding: '12px' }}>{agent.totalConversations}</td>
                      <td style={{ padding: '12px' }}>{agent.completedConversations}</td>
                      <td style={{ padding: '12px' }}>{agent.completionRate.toFixed(1)}%</td>
                      <td style={{ padding: '12px' }}>{agent.averageResponseTime.toFixed(0)}ms</td>
                      <td style={{ padding: '12px' }}>{agent.typeProduct}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default AgentPerformanceChart;
